import React from 'react';
import { useOperatorProficiencyTable } from '@/hooks/AssetsPreviewPage/OperatorProficiency';
import { useOperatorProficiency } from '@/contexts/OperatorProficiencyContext';
import { Skeleton } from '@/components/ui/skeleton';
import { BaseTable } from '@/components/uiKit/Tables/BaseTable';
import { FleetAbuseChart } from './components/FleetAbuseChart';
import { FleetMostFrequentCard } from './components/FleetMostFrequentCard';
import { Users, AlertCircle } from 'lucide-react';

/**
 * Operator Proficiency Page
 * Uses context provided by parent (AssetsPreviewPage wraps with OperatorProficiencyProvider)
 */
export const OperatorProficiencyPage: React.FC = () => {
  const { tableStructure } = useOperatorProficiencyTable();
  const { data, loading, dataLayers, error } = useOperatorProficiency();

  // Check if we have no data available or API error (e.g., 404)
  const hasError = !loading && !!error;
  const hasNoDataLayers = !loading && (!dataLayers || dataLayers.length === 0);
  const hasNoData = !loading && (!data || data.length === 0);
  const showEmptyState = hasError || hasNoDataLayers || hasNoData;

  // Empty state component
  if (showEmptyState) {
    const isError = hasError;
    const Icon = isError ? AlertCircle : Users;
    const message = isError
      ? 'Something went wrong. Please try again later.'
      : 'Operator Proficiency Data Not Available.';

    return (
      <div className="flex flex-col min-h-[calc(100vh-12rem)] w-full items-center justify-start pt-32">
        <div className="flex flex-col items-center gap-4 text-center p-8">
          <div
            className={`rounded-full p-6 ${isError ? 'bg-red-100 dark:bg-red-900/20' : 'bg-gray-100 dark:bg-gray-800'}`}
          >
            <Icon className={`h-12 w-12 ${isError ? 'text-red-400' : 'text-gray-400'}`} />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">{message}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex flex-1 gap-4 overflow-hidden">
        {/* Main Table Section - 75% width */}
        <div className="h-[calc(100vh-12rem)] overflow-hidden" style={{ width: '75%' }}>
          {loading ? (
            <Skeleton className="h-full w-full rounded-lg" />
          ) : (
            <BaseTable
              enableSearch={true}
              enableSearchDropdown={false}
              searchPlaceholder="Search drivers..."
              data={data || []}
              columns={tableStructure}
              wrapHeaders={false}
              enableSorting={true}
              defaultSortId="eventsCount"
              variant="spaced"
              pageSize={999999}
              classNames={{
                card: {
                  container: 'h-full w-full overflow-hidden flex flex-col',
                  content: 'p-0 m-2 flex-1 overflow-hidden flex flex-col min-h-0',
                },
                table: {
                  container: 'overflow-y-auto min-h-0 w-full scrollbar-thin max-h-[calc(100vh-16rem)]',
                  th: '[&>*]:text-subtext [&>*]:font-medium [&>*]:text-xs py-2 first-of-type:w-[40px]',
                  tr: 'bg-white dark:bg-[#52606d] rounded-lg !cursor-grab hover:!cursor-grab active:!cursor-grabbing',
                  td: 'first-of-type:w-[40px] first:rounded-l-lg last:rounded-r-lg text-sm',
                },
              }}
            />
          )}
        </div>

        {/* Sidebar Section - 25% width */}
        <div className="h-[calc(100vh-12rem)]" style={{ width: '25%' }}>
          <div className="flex flex-col h-full gap-3">
            {loading ? (
              <>
                <div className="h-[220px] bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
                  <Skeleton className="h-full w-full rounded-lg" />
                </div>
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm border overflow-hidden">
                  <Skeleton className="h-full w-full rounded-lg" />
                </div>
              </>
            ) : (
              <>
                {/* Fleet Abuse Chart */}
                <div className="h-[220px] bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
                  <FleetAbuseChart className="h-full" />
                </div>

                {/* Fleet Most Frequent Card */}
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm border overflow-hidden">
                  <FleetMostFrequentCard className="h-full" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
